import { useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getItem,
  getMediaSegments,
  getPlayerPlaybackInfo,
} from "../api/jellyfin";
import type { Credentials } from "../storage/credentials";
import { mergePlaybackSources } from "./sourceCatalog";
import {
  PLAYER_PLAYBACK_INFO_STALE_MS,
  PLAYER_SOURCE_CATALOG_STALE_MS,
  playerPlaybackInfoQueryKey,
  playerSourceCatalogQueryKey,
} from "./playbackQueryKeys";
import {
  getPlaybackInfoRetryDelay,
  shouldRetryPlaybackInfoRequest,
} from "./playbackRetry";
import { productProfile } from "../productProfile";

type PlayerMediaQueriesInput = {
  credentials: Credentials | null;
  itemId: string;
  selectedSourceId: string;
  explicitSourceId?: string;
  decoderReady: boolean;
  versionsRequested: boolean;
};

/** Owns startup query ordering and progressive source-catalog hydration. */
export function usePlayerMediaQueries({
  credentials,
  itemId,
  selectedSourceId,
  explicitSourceId,
  decoderReady,
  versionsRequested,
}: PlayerMediaQueriesInput) {
  const itemQuery = useQuery({
    queryKey: ["player", "item", itemId],
    queryFn: () => getItem(credentials!, itemId),
    enabled: Boolean(credentials),
    // A background refetch must never mutate the active playback generation:
    // fresh item data recomputes the resume offset, which changes the stream
    // URI, remounts the decoder, and starts a new server session. Refetches
    // still happen on mount and via explicit queryClient operations.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  // Sticky PlaybackInfo source during the startup window.  When recovery
  // advances to a new source before the decoder is ready, the query key must
  // NOT change: a new key creates a new PlaySessionId, the active-encoding
  // lease closes the in-flight transcode, and the old playlist 404s.  That
  // cascade produces another recovery, creating a loop that never lets a
  // transcode warm up.  Lag the PlaybackInfo source so the current mount gets
  // its full startup window before the next scoped request.
  //
  // When the decoder does become ready, discard any pending recovery source.
  // The mount survived — keep it.  Only adopt a replacement source when the
  // decoder is already healthy (deliberate track/version change, not a
  // startup rescue attempt).
  const committedSourceRef = useRef<string>(explicitSourceId ?? "");
  const pendingSourceRef = useRef<string | undefined>(undefined);
  const requestedSourceId = selectedSourceId || explicitSourceId;
  if (requestedSourceId && requestedSourceId !== committedSourceRef.current) {
    if (!committedSourceRef.current || decoderReady) {
      committedSourceRef.current = requestedSourceId;
      pendingSourceRef.current = undefined;
    } else {
      // Decoder is still starting with a known source — queue the request
      // but don't kill the current mount.  If the decoder survives, the
      // pending source is discarded on the next decoderReady transition.
      pendingSourceRef.current = requestedSourceId;
    }
  }
  // When the decoder becomes ready, discard any pending recovery.  The mount
  // survived its startup window — don't replace it with a recovery candidate.
  const prevDecoderReadyRef = useRef(decoderReady);
  if (decoderReady && !prevDecoderReadyRef.current && pendingSourceRef.current) {
    pendingSourceRef.current = undefined;
  }
  prevDecoderReadyRef.current = decoderReady;
  // Reset on item change.
  const committedSourceRefItem = useRef(itemId);
  if (committedSourceRefItem.current !== itemId) {
    committedSourceRefItem.current = itemId;
    committedSourceRef.current = explicitSourceId ?? "";
    pendingSourceRef.current = undefined;
  }
  const playbackRequestSourceId = committedSourceRef.current;
  const itemHasProvisionalSources = Boolean(itemQuery.data?.MediaSources?.length);
  const playbackQuery = useQuery({
    queryKey: playerPlaybackInfoQueryKey(itemId, playbackRequestSourceId),
    queryFn: () => getPlayerPlaybackInfo(
      credentials!,
      itemId,
      playbackRequestSourceId,
    ),
    // Let source ranking choose from provisional item metadata before probing.
    // This avoids launching an expensive unscoped request that is immediately
    // replaced by a second request for the selected source.
    enabled: Boolean(
      credentials &&
      itemQuery.data &&
      (playbackRequestSourceId || !itemHasProvisionalSources),
    ),
    // Remote catalog sources can all fail their first cold probe, then become
    // usable as Remux finishes and caches a concurrent/later probe. Keep this
    // startup mount alive for bounded transient retries; never retry auth and
    // other ordinary 4xx responses.
    retry: shouldRetryPlaybackInfoRequest,
    retryDelay: getPlaybackInfoRetryDelay,
    staleTime: PLAYER_PLAYBACK_INFO_STALE_MS,
    // A background refetch must never mutate the active playback generation:
    // fresh PlaybackInfo carries a new PlaySessionId, which changes the stream
    // URI, remounts the decoder, and starts a new server session. Refetches
    // still happen on mount and via the coordinator's explicit refetch()
    // (playback retry / recovery paths).
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const sourceCatalogQuery = useQuery({
    queryKey: playerSourceCatalogQueryKey(itemId),
    queryFn: () => getPlayerPlaybackInfo(credentials!, itemId),
    // The selected source owns startup. Hydrate alternatives after readiness or
    // on explicit Versions demand, without replacing the active decoder.
    enabled: Boolean(
      credentials &&
      itemQuery.data &&
      productProfile.aggressiveOptimizationsEnabled &&
      (decoderReady || versionsRequested),
    ),
    retry: 0,
    staleTime: PLAYER_SOURCE_CATALOG_STALE_MS,
    // See playbackQuery: background refetches must not churn the active
    // generation (catalog re-rank can cascade into a source/session swap).
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const segmentsQuery = useQuery({
    queryKey: ["player", "segments", itemId],
    queryFn: () => getMediaSegments(credentials!, itemId),
    // Intro/outro metadata never blocks transport startup. Avoid competing
    // with scoped PlaybackInfo and the first fragment on a cold item.
    enabled: Boolean(credentials && decoderReady),
    retry: 0,
  });
  const sourceCacheRef = useRef<{ itemId: string; sources: ReturnType<typeof mergePlaybackSources> }>({
    itemId,
    sources: [],
  });
  if (sourceCacheRef.current.itemId !== itemId) {
    sourceCacheRef.current = { itemId, sources: [] };
  }
  const probedSources = useMemo(() => {
    const sources = mergePlaybackSources(
      sourceCacheRef.current.sources,
      sourceCatalogQuery.data?.MediaSources,
      playbackQuery.data?.MediaSources,
    );
    sourceCacheRef.current = { itemId, sources };
    return sources;
  }, [itemId, playbackQuery.data?.MediaSources, sourceCatalogQuery.data?.MediaSources]);

  return {
    itemQuery,
    playbackQuery,
    sourceCatalogQuery,
    segmentsQuery,
    probedSources,
  };
}
